// Suggested test inputs, read from the config alone: nothing from stored client data, and nothing
// runs until Send. Values are keyed like the workspace's test values (args.<name>).
const MAX_EACH_OPTION = 3;
const NO_MATCH = 'no-match';

const CLASSES = {
  '\\d': ['0', '9'],
  '[0-9]': ['0', '9'],
  '[a-z]': ['a', 'z'],
  '[A-Z]': ['A', 'Z'],
};
const TOKEN = /\\d|\[0-9\]|\[a-z\]|\[A-Z\]|\\.|[^\\[\]{}()|+*?.^$]/y;
const COUNT = /\{(\d+)(?:,(\d*))?\}|[+*?]/y;

// A sample for a simple pattern (^[0-9]{6}$ → 000000; high: 999999, which a real id rarely is);
// null for anything with alternation, groups or other classes.
export const patternSample = (pattern, { high = false } = {}) => {
  const body = String(pattern || '')
    .replace(/^\^/, '')
    .replace(/\$$/, '');
  if (!body) return null;
  let out = '';
  let at = 0;
  while (at < body.length) {
    TOKEN.lastIndex = at;
    const token = TOKEN.exec(body);
    if (!token) return null;
    at = TOKEN.lastIndex;
    const char = CLASSES[token[0]]?.[high ? 1 : 0] ?? (token[0].startsWith('\\') ? token[0].slice(1) : token[0]);
    COUNT.lastIndex = at;
    const count = COUNT.exec(body);
    let times = 1;
    if (count) {
      at = COUNT.lastIndex;
      times = count[1] !== undefined ? Number(count[1]) : count[0] === '+' ? 1 : 0;
    }
    out += char.repeat(times);
  }
  return out;
};

const enumValues = (row) =>
  row.enumValues
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

// The input a row suggests: an enum's first value, false, the lowest number, a pattern sample;
// '' for free text, where the pattern itself shows as the placeholder.
export const suggestedValue = (row) => {
  if (row.type === 'boolean') return false;
  if (row.type === 'enum') return enumValues(row)[0] || '';
  // No bounds are declared on a number, so the lowest that reads as a quantity.
  if (row.type === 'number') return '1';
  return patternSample(row.pattern) ?? '';
};

const blank = (row) => (row.type === 'boolean' ? false : '');
const keyOf = (row) => `args.${row.name.trim()}`;

// Scenario chips: each fills every argument at once. Typical keeps a value already typed.
export const probeScenarios = (form, testValues = {}) => {
  const rows = form.tool.argRows.filter((row) => row.name.trim());
  if (!rows.length) return [];

  const typicalOf = (row) => {
    const current = testValues[keyOf(row)];
    return current === undefined || current === '' ? suggestedValue(row) : current;
  };
  const fill = (valueOf) => Object.fromEntries(rows.map((row) => [keyOf(row), valueOf(row)]));
  const scenarios = [{ id: 'typical', label: 'Typical', values: fill(typicalOf) }];

  if (rows.some((row) => !row.required))
    scenarios.push({
      id: 'required',
      label: 'Required only',
      values: fill((row) => (row.required ? typicalOf(row) : blank(row))),
    });

  const filtered = new Set(
    form.filterRows.map((rule) => /^\{\{\s*args\.([^}\s]+)\s*\}\}$/.exec(rule.value.trim())?.[1]).filter(Boolean)
  );
  const missWith = rows.filter((row) => row.type === 'string' && (filtered.has(row.name.trim()) || !filtered.size));
  if ((filtered.size || form.notFoundPath.trim()) && missWith.length) {
    const target = filtered.size ? missWith : missWith.slice(0, 1);
    scenarios.push({
      id: 'none',
      label: 'Nothing matches',
      values: fill((row) =>
        target.includes(row)
          ? row.pattern
            ? (patternSample(row.pattern, { high: true }) ?? NO_MATCH)
            : NO_MATCH
          : typicalOf(row)
      ),
    });
  }

  rows
    .filter((row) => row.type === 'enum')
    .forEach((row) => {
      const options = enumValues(row);
      if (options.length < 2 || options.length > MAX_EACH_OPTION) return;
      options.forEach((option) =>
        scenarios.push({
          id: `option:${row.name.trim()}:${option}`,
          label: `${row.name.trim()}: ${option}`,
          group: 'Each option',
          values: fill((other) => (other === row ? option : typicalOf(other))),
        })
      );
    });

  return scenarios;
};
